import type { ReactNode } from 'react';
import {
  type Control,
  type FieldError,
  type FieldErrors,
  Controller,
  useWatch,
} from 'react-hook-form';
import {
  FormControl,
  RadioGroup,
  FormControlLabel,
  Radio,
  FormHelperText,
} from '@mui/material';
import Grid from '@mui/material/Grid2';
import type { Location } from '../types';
import SectionHeading from './SectionHeading';
import FieldLabel from './FieldLabel';
import LabeledDatePicker from './LabeledDatePicker';
import LocationSelect from './LocationSelect';
import { GRID_SPACING, COL_2 } from '../constants/layout';
import FormSection from './FormSection';
import { ENCOUNTER_INFO_FIELDS, type FormFieldSetting } from '../constants/encounterFieldsConfig';
import { hasFieldValue } from '../utils/hasFieldValue';

export type EncounterInfoFieldKey = (typeof ENCOUNTER_INFO_FIELDS)[number];

const DEFAULT_SETTING: FormFieldSetting = { visible: true, required: true };

interface EncounterInfoSectionProps {
  control: unknown;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  errors: FieldErrors<any>;
  locations: Location[];
  locationsLoading: boolean;
  locationsError: string | null;
  /**
   * Feature 043 — per-field visibility/required from practice settings, keyed the
   * same way as `EncounterFieldsConfig.encounterFields`. A field omitted from the
   * map (or an omitted `fields` prop entirely) defaults to visible+required, so
   * edit-claim's usage — which passes no `fields` — is unaffected.
   *
   * `isGapCase` also hides for Office-Visit-only encounters regardless of config —
   * that's a page-level rule (gap case never applies to Office Visit), so callers
   * fold it into `fields.isGapCase.visible` before passing this prop down rather
   * than this component knowing about claim types.
   */
  fields?: Partial<Record<EncounterInfoFieldKey, FormFieldSetting>>;
  /** Optional banner rendered inside the section (e.g. shared-changes warning). */
  warning?: ReactNode;
}

/**
 * Shared "Encounter Information" section (feature 034) used by both submit-claim
 * and edit-claim. Fields: Date of Service*, Location*, Is this a gap case? (No/Yes).
 *
 * `resolved` is typed `Record<EncounterInfoFieldKey, FormFieldSetting>` — sourced
 * from `ENCOUNTER_INFO_FIELDS` (the same list `encounterFieldsConfig.ts` uses to
 * build `ENCOUNTER_CONFIGURABLE_KEYS`), so adding a field there without adding a
 * line here is a compile error, not a silently-unrendered field.
 */
export default function EncounterInfoSection({
  control,
  errors: errs,
  locations,
  locationsLoading,
  locationsError,
  fields,
  warning,
}: EncounterInfoSectionProps) {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const ctrl = control as Control<any>;

  // Feature 043 — a field with a saved value stays visible even when practice
  // settings mark it not-visible (hiding real data would look like data loss).
  // isGapCase is excluded: it's a No/Yes radio where `false` is both a legitimate
  // "No" answer and the value the page forces onto a hidden field, so a saved
  // value can't be distinguished from "never shown" — visibility stays config-only.
  const [watchedDateOfService, watchedLocation] = useWatch({ control: ctrl, name: ['dateOfService', 'location'] });

  const resolved: Record<EncounterInfoFieldKey, FormFieldSetting> = {
    dateOfService: fields?.dateOfService ?? DEFAULT_SETTING,
    location: fields?.location ?? DEFAULT_SETTING,
    isGapCase: fields?.isGapCase ?? DEFAULT_SETTING,
  };
  const dateOfService = { ...resolved.dateOfService, visible: resolved.dateOfService.visible || hasFieldValue(watchedDateOfService) };
  const location = { ...resolved.location, visible: resolved.location.visible || hasFieldValue(watchedLocation) };
  const isGapCase = resolved.isGapCase;

  if (!dateOfService.visible && !location.visible && !isGapCase.visible) {
    return null;
  }

  return (
    <FormSection>
      <SectionHeading title="Encounter Details" />
      {warning}
      <Grid container spacing={GRID_SPACING}>
        {dateOfService.visible && (
          <Grid size={COL_2}>
            <Controller
              name="dateOfService"
              control={ctrl}
              render={({ field }) => (
                <LabeledDatePicker
                  label="Date of Service"
                  required={dateOfService.required}
                  value={field.value ?? ''}
                  onChange={field.onChange}
                  error={!!errs.dateOfService}
                  helperText={errs.dateOfService?.message as string | undefined}
                  maxDate={new Date()}
                />
              )}
            />
          </Grid>
        )}
        {location.visible && (
          <Grid size={COL_2}>
            <LocationSelect
              control={ctrl}
              error={errs.location as FieldError | undefined}
              locations={locations}
              loading={locationsLoading}
              loadError={locationsError}
              required={location.required}
            />
          </Grid>
        )}
        {isGapCase.visible && (
          <Grid size={COL_2}>
            <FieldLabel required={isGapCase.required}>Is this a gap case?</FieldLabel>
            <FormControl component="fieldset" error={!!errs.isGapCase} fullWidth>
              <Controller
                name="isGapCase"
                control={ctrl}
                render={({ field }) => (
                  <RadioGroup
                    row
                    aria-label="is-gap-case"
                    value={
                      field.value === undefined || field.value === null
                        ? ''
                        : String(field.value)
                    }
                    onChange={(e) => field.onChange(e.target.value === 'true')}
                    sx={{ height: 36, gap: 2, flexWrap: 'nowrap', alignItems: 'center' }}
                  >
                    <FormControlLabel
                      value="false"
                      control={<Radio size="small" sx={{ color: 'divider', '&.Mui-checked': { color: 'primary.main' } }} />}
                      label="No"
                      sx={{ mr: 1, '& .MuiFormControlLabel-label': { fontSize: 14 } }}
                    />
                    <FormControlLabel
                      value="true"
                      control={<Radio size="small" sx={{ color: 'divider', '&.Mui-checked': { color: 'primary.main' } }} />}
                      label="Yes"
                      sx={{ mr: 1, '& .MuiFormControlLabel-label': { fontSize: 14 } }}
                    />
                  </RadioGroup>
                )}
              />
              {errs.isGapCase && (
                <FormHelperText sx={{ ml: 0 }}>{errs.isGapCase.message as string}</FormHelperText>
              )}
            </FormControl>
          </Grid>
        )}
      </Grid>
    </FormSection>
  );
}
